import { useEffect, useState, useMemo } from 'react';
import { Users, Activity, Leaf, AlertTriangle, Droplet } from 'lucide-react';
import { KPICard } from './components/KPICard';
import { FilterBar } from './components/FilterBar';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { useFilters } from './components/FilterContext';
import apiClient from '../../utils/axios.js';
import { transformDashboardData } from '../../utils/dashboardDataTransform';

export function Overview() {
  const { filters } = useFilters();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/admin/dashboard-data');
      const transformedData = transformDashboardData(response.data.dashboardData || []);
      setData(transformedData);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.response?.data?.msg || 'Failed to load dashboard data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }

  const filteredData = useMemo(() => {
    if (!data.length) return [];
    
    return data.filter(item => {
      if (filters.villages && filters.villages.length > 0) {
        if (!filters.villages.includes(item.farmers?.village)) return false;
      }
      
      if (filters.previousCrop && filters.previousCrop.length > 0) {
        if (!filters.previousCrop.includes(item.previous_crop)) return false;
      }

      if (filters.farmSize && filters.farmSize.length > 0) {
        const size = parseFloat(item.farm_size);
        if (isNaN(size)) return false; 

        const matchesSize = filters.farmSize.some(filterOption => {
          if (filterOption === '< 1 acre') return size < 1;
          if (filterOption === '1-2 acres') return size >= 1 && size <= 2;
          if (filterOption === '> 2 acres') return size > 2;
          return false;
        });

        if (!matchesSize) return false;
      }

      return true;
    });
  }, [data, filters]);

  const uniqueFarmersData = useMemo(() => {
    const map = new Map();
    
    filteredData.forEach(item => {
      const uniqueKey = item.farmer_id; 
      if (uniqueKey && !map.has(uniqueKey)) {
        map.set(uniqueKey, item); 
      }
    });
    
    return Array.from(map.values());
  }, [filteredData]);

  const totalFarmers = uniqueFarmersData.length;
  const villages = uniqueFarmersData.length > 0 ? new Set(uniqueFarmersData.map(d => d.farmers?.village).filter(Boolean)).size : 0;

  const avgScore =
    uniqueFarmersData.length > 0
      ? Math.round(
        uniqueFarmersData.reduce((sum, d) => sum + (d.soil_health_score || 0), 0) /
        uniqueFarmersData.length
      )
      : 0;

  const lowOCCount = uniqueFarmersData.filter(
    d => d.organic_carbon !== null && d.organic_carbon < 0.8
  ).length;
  const lowOCPercent =
    uniqueFarmersData.length > 0 ? Math.round((lowOCCount / uniqueFarmersData.length) * 100) : 0;

  const highpHCount = uniqueFarmersData.filter(
    d => d.ph !== null && (d.ph < 6.5 || d.ph > 7.5)
  ).length;
  const highpHPercent =
    uniqueFarmersData.length > 0 ? Math.round((highpHCount / uniqueFarmersData.length) * 100) : 0;

  const microDefCount = uniqueFarmersData.filter(
    d =>
      (d.zinc !== null && d.zinc < 0.5) ||
      (d.iron !== null && d.iron < 5.0) ||
      (d.boron !== null && d.boron < 0.1) ||
      (d.copper !== null && d.copper < 0.2)
  ).length;
  const microDefPercent =
    uniqueFarmersData.length > 0 ? Math.round((microDefCount / uniqueFarmersData.length) * 100) : 0;

  const issueDistribution = {
    'Low OC dominant': uniqueFarmersData.filter(d => d.primary_issue === 'Low OC dominant').length,
    'High pH dominant': uniqueFarmersData.filter(d => d.primary_issue === 'High pH dominant').length,
    'Macro imbalance (NPK)': uniqueFarmersData.filter(d => d.primary_issue === 'Macro imbalance (NPK)').length,
    'Micronutrient deficiency dominant': uniqueFarmersData.filter(
      d => d.primary_issue === 'Micronutrient deficiency dominant'
    ).length,
    'Balanced/No major issue': uniqueFarmersData.filter(
      d => d.primary_issue === 'Balanced/No major issue'
    ).length,
  };

  const villageStats = Array.from(
    new Set(uniqueFarmersData.map(d => d.farmers?.village))
  )
    .filter(Boolean)
    .map(village => {
      const villageData = uniqueFarmersData.filter(d => d.farmers?.village === village);
      const avgScore =
        villageData.length > 0
          ? Math.round(
            villageData.reduce(
              (sum, d) => sum + (d.soil_health_score || 0),
              0
            ) / villageData.length
          )
          : 0;

      const lowOC = Math.round(
        (villageData.filter(
          d => d.organic_carbon !== null && d.organic_carbon < 0.8
        ).length /
          villageData.length) *
        100
      );

      const highpH = Math.round(
        (villageData.filter(
          d => d.ph !== null && d.ph > 7.5
        ).length /
          villageData.length) *
        100
      );

      return {
        village,
        samples: villageData.length,
        avgScore,
        lowOCPercent: lowOC,
        highpHPercent: highpH,
      };
    })
    .sort((a, b) => a.avgScore - b.avgScore)
    .slice(0, 10);

  const getScoreColor = score => {
    if (score < 40) return 'amber';
    if (score < 70) return 'amber';
    return 'green';
  };

  if (loading) {
    return (
      <>
        <FilterBar data={data} />
        <LoadingSkeleton />
      </>
    );
  }

  if (error) {
    return (
      <div className="min-h-full bg-[#F5F5F5]">
        <FilterBar data={data} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F5F5F5]">
      <FilterBar data={data}/>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6 sm:mb-8">
          <KPICard
            title="Farmers Tested"
            value={totalFarmers}
            subtitle={`Villages: ${villages}`}
            icon={Users}
            color="green"
          />
          <KPICard
            title="Avg Soil Health Score"
            value={avgScore}
            subtitle={avgScore < 40 ? 'Poor' : avgScore < 70 ? 'Fair' : 'Good'}
            icon={Activity}
            color={getScoreColor(avgScore)}
            bgTint
          />
          <KPICard
            title="Farms With Low Organic Carbon"
            value={`${lowOCPercent}%`}
            subtitle="OC < 0.8"
            icon={Leaf}
            color="amber"
          />
          <KPICard
            title="Farms With Problematic pH"
            value={`${highpHPercent}%`}
            subtitle="pH < 6.5 or > 7.5"
            icon={AlertTriangle}
            color={highpHPercent > 60 ? 'red' : 'amber'}
          />
          <KPICard
            title="Farms With Micronutrient Deficiency"
            value={`${microDefPercent}%`}
            subtitle="At least one deficiency"
            icon={Droplet}
            color="teal"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          <IssueCompositionChart data={issueDistribution} total={uniqueFarmersData.length} />
          <VillageHealthChart villages={villageStats} />
        </div>

        <UrgentVillagesTable villages={villageStats} />
      </div>
    </div>
  );
}

function IssueCompositionChart({ data, total }) {
  const colors = {
    'Balanced/No major issue': '#2E7D32',
    'Low OC dominant': '#FFA000',
    'High pH dominant': '#D32F2F',
    'Macro imbalance (NPK)': '#7B1FA2',
    'Micronutrient deficiency dominant': '#0097A7',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Soil Issue Composition
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Distribution of the primary soil health issues identified across all tested farmers.
      </p>
      <div className="space-y-3">
        {Object.entries(data).map(([issue, count]) => {
          const percent = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={issue}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">{issue}</span>
                <span className="text-gray-600">
                  {count} ({percent}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${percent}%`,
                    backgroundColor: colors[issue] || '#9E9E9E',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VillageHealthChart({ villages }) {
  const maxSamples = Math.max(...villages.map(v => v.samples), 1);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Village Coverage vs Health
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Compare the number of farmers tested in each village with their average soil health score.
      </p>
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {villages.map(village => (
          <div key={village.village} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700 font-medium">
                {village.village}
              </span>
              <span className="text-gray-600">
                Score: {village.avgScore}
              </span>
            </div>
            <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
              <div
                className="h-6 rounded-full flex items-center justify-end px-2 text-xs font-medium text-white"
                style={{
                  width: `${(village.samples / maxSamples) * 100}%`,
                  backgroundColor:
                    village.avgScore < 40
                      ? '#D32F2F'
                      : village.avgScore < 70
                        ? '#FFA000'
                        : '#2E7D32',
                }}
              >
                {village.samples}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UrgentVillagesTable({ villages }) {
  const getScoreBadgeClass = (score) => {
    if (score < 40) return 'bg-red-100 text-red-800';
    if (score < 70) return 'bg-amber-100 text-amber-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
        Villages Needing Urgent Focus
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Villages ranked by soil health indicators to help prioritize interventions and field activities.
      </p>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Village</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Samples</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">Score</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">% Low OC</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 whitespace-nowrap">% Problematic pH</th>
              </tr>
            </thead>
            <tbody>
              {villages.map(v => (
                <tr key={v.village} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">{v.village}</td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{v.samples}</td>
                  <td className="py-3 px-4 text-sm whitespace-nowrap">
                    <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getScoreBadgeClass(v.avgScore)}`}>
                      {v.avgScore}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{v.lowOCPercent}%</td>
                  <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{v.highpHPercent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}